import imgGraphDb from '@/assets/projects/graph-db.svg'
import imgGraphMemory from '@/assets/projects/graph-memory.svg'
import imgFuzzyReview from '@/assets/projects/fuzzy-review.svg'
import imgUav from '@/assets/projects/uav.svg'
import imgPidRobot from '@/assets/projects/pid-robot.svg'
import imgRosMapping from '@/assets/projects/ros-mapping.svg'
import imgDmuRag from '@/assets/projects/dmu-rag.svg'

/**
 * Featured MSc AI projects — list cards + detail write-ups.
 * Media on detail pages is placeholder until real assets are ready.
 *
 * Set to false to hide project detail links/routes without removing content.
 */
export const projectDetailPagesEnabled = false

export const featuredProjects = [
  {
    slug: 'graph-db',
    title: 'Graph Database Engine',
    status: 'In progress',
    description:
      'High-performance graph database in C with ACID transactions, WAL persistence, bitmap indexes, and vector search — an experimental platform for AI long-term memory research and the storage engine behind my MSc dissertation.',
    image: imgGraphDb,
    tech_stack: [
      'C',
      'ACID / WAL',
      'Bitmap indexes',
      'Vector search',
      'Graph databases'
    ],
    code: null,
    website: null,
    overview: [
      'A high-performance graph database engine written in C, designed for production-style durability and for research workloads that need both relational structure and vector similarity.',
      'It serves as the underlying storage engine for my MSc research on LLM-aware graph memory, while remaining a standalone systems project.'
    ],
    approach: [
      'Implement ACID transactions with write-ahead log (WAL) persistence for crash safety and recoverable state.',
      'Use bitmap indexes for efficient set operations over graph structure, and add vector search so embeddings can live alongside graph data.',
      'Evolve the engine continuously as a platform for AI long-term memory experiments and production applications.'
    ],
    results: [
      'Work is ongoing as both a systems project and the storage substrate for the LLM-aware graph memory dissertation.',
      'Focus areas include transactional correctness, index performance, and vector-aware retrieval over graph-structured memory.'
    ],
    media: {
      images: [{ label: 'Architecture / storage layout', ready: false }],
      videos: [{ label: 'Query walkthrough', ready: false }]
    }
  },
  {
    slug: 'graph-memory',
    title: 'LLM-Aware Graph Memory',
    status: 'In progress',
    description:
      'MSc dissertation: graph-based long-term memory for LLM agents using semantic retrieval and graph traversal, evaluating retrieval quality, token efficiency, scalability, and persistent knowledge storage.',
    image: imgGraphMemory,
    tech_stack: [
      'Graph databases',
      'LLMs',
      'Semantic retrieval',
      'Agentic AI',
      'Research'
    ],
    code: null,
    website: null,
    overview: [
      'Researching graph-based long-term memory architectures for LLM agents using semantic retrieval and graph traversal.',
      'Investigating graph databases as scalable long-term memory backends for persistent and context-aware AI agents.'
    ],
    approach: [
      'Model memory as an evolving graph of memory traces. Nodes represent entities, concepts, events, actions, and higher-level traces; edges capture semantic, episodic, temporal, causal, and procedural links.',
      'Treat retrieval as graph traversal and pathfinding with reinforcement, forgetting, and recall-triggered updates so the graph changes as it is used.',
      'Evaluate retrieval quality, token efficiency, scalability, and persistent knowledge storage against vector-only baselines.'
    ],
    results: [
      'Work is in progress as the MSc thesis project. Research interests include AI memory systems (semantic, episodic & procedural), cognitive architectures, agentic AI, reinforcement learning, knowledge representation, and graph databases.',
      'A planned PhD extension would deepen procedural and episodic memory subsystems on the same graph substrate.'
    ],
    media: {
      images: [{ label: 'Architecture diagram', ready: false }],
      videos: [{ label: 'Retrieval walkthrough', ready: false }]
    }
  },

  {
    slug: 'fuzzy-code-review',
    title: 'Fuzzy Code Review',
    description:
      'Type-1 Mamdani fuzzy inference for merge recommendations from complexity, test coverage, memory safety, and error handling.',
    image: imgFuzzyReview,
    tech_stack: [
      'Fuzzy logic',
      'Mamdani FIS',
      'MATLAB',
      'Code review',
      'Decision support'
    ],
    code: null,
    website: null,
    overview: [
      'Code review decisions are rarely fully binary. Reviewers combine measurable metrics with judgement under uncertainty: conflicting signals, boundary cases, and risk that crisp thresholds handle poorly.',
      'This project designs a Type-1 Mamdani fuzzy inference system that maps software quality indicators to a continuous merge recommendation score, acting as an interpretable assistant rather than a black-box gate.'
    ],
    approach: [
      'Four normalised inputs (0–100): complexity, test coverage, memory safety, and error handling quality. The output is a merge recommendation with linguistic labels from Reject through Approve.',
      'A risk-first rule base prioritises safety-dominant cases, then models trade-offs between complexity and coverage, with mitigation rules when other evidence is strong.',
      'The MATLAB implementation compares three membership-function families (triangular, trapezoidal, Gaussian) and three defuzzification methods (centroid, bisector, mean of maximum).'
    ],
    results: [
      'Overall behaviour was stable across membership families, with expected differences around boundary cases.',
      'Type-1 Mamdani inference offered a practical balance between rigid threshold rules and less interpretable data-driven scorers, suitable as decision support for review teams that need explainability.'
    ],
    media: {
      images: [
        { label: 'Membership functions', ready: false },
        { label: 'Scenario score surface', ready: false }
      ],
      videos: [{ label: 'Scoring walkthrough', ready: false }]
    }
  },
  {
    slug: 'uav-object-recognition',
    title: 'UAV Object Recognition',
    description:
      'Compared crop-and-classify ResNet-50 (99.4% accuracy, 98.5% macro F1) with COCO-pretrained DETR (71.5% mAP@0.5) for multi-class road-user recognition on annotated UAV video.',
    image: imgUav,
    tech_stack: [
      'ResNet-50',
      'DETR',
      'PyTorch',
      'Object detection',
      'UAV / VisDrone'
    ],
    code: null,
    website: null,
    overview: [
      'UAV footage supports traffic monitoring and urban analytics. With per-frame bounding-box labels, one path is multi-class classification of cropped objects; another is end-to-end detection that predicts location and category together.',
      'This project compares both on sequence uav0000297_00000_v: an ImageNet-pretrained ResNet-50 crop-and-classify baseline and a COCO-pretrained DETR with a ResNet-50 backbone.'
    ],
    approach: [
      'Dataset: 8,903 labelled instances across VisDrone-style classes from 146 frames, with shared augmentation (horizontal flip, colour jitter). Classification used an 80/20 crop split; detection used a frame-level split.',
      'ResNet-50 trained for six epochs on crops. DETR was fine-tuned for set-based detection so localisation and classification are scored jointly (mAP@0.5, mean IoU).'
    ],
    results: [
      'ResNet-50 reached 99.4% accuracy and 98.5% macro F1 on held-out crops.',
      'Fine-tuned DETR reached 71.5% mAP@0.5 and 0.71 mean IoU on held-out frames.',
      'The write-up also covers class imbalance, GPU memory limits, and how crop pipelines differ from end-to-end detection in practice.'
    ],
    media: {
      images: [
        { label: 'Detection overlays', ready: false },
        { label: 'Confusion matrix / metrics', ready: false }
      ],
      videos: [{ label: 'UAV sequence with predictions', ready: false }]
    }
  },
  {
    slug: 'pid-mobile-robot',
    title: 'PID Mobile Robot Control',
    description:
      'Built a PID controller for a mobile robot in CoppeliaSim covering sensing, actuation, and closed-loop navigation in a simulated world.',
    image: imgPidRobot,
    tech_stack: ['PID control', 'CoppeliaSim', 'Python', 'Mobile robots'],
    code: null,
    website: null,
    overview: [
      'Mobile robot control needs stable closed-loop behaviour: sense the world, compute error against a setpoint, and actuate wheels or joints without oscillation or steady-state drift.',
      'This project implements PID control for a mobile robot in CoppeliaSim, connecting simulation sensors and actuators into a navigable control loop.'
    ],
    approach: [
      'Tune proportional, integral, and derivative gains for the simulated plant, and drive the robot through navigation tasks in the CoppeliaSim environment.',
      'Focus on observable behaviour (path tracking, response to disturbances, and qualitative stability) as the basis for evaluation.'
    ],
    results: [
      'A working PID controller that navigates the simulated world under closed-loop control.',
      'Demo media (before/after clips and short navigation videos) will be added here once exports from CoppeliaSim are ready.'
    ],
    media: {
      images: [
        { label: 'Scene / path overlay', ready: false },
        { label: 'Before vs after tracking', ready: false }
      ],
      videos: [{ label: 'Navigation clip', ready: false }]
    }
  },
  {
    slug: 'ros-localisation-mapping',
    title: 'ROS Localisation & Mapping',
    description:
      'Evaluated AMCL vs Gmapping localisation on TurtleBot3 in Gazebo/RViz, and implemented Bayesian occupancy-grid mapping from laser scans and odometry.',
    image: imgRosMapping,
    tech_stack: [
      'ROS',
      'AMCL',
      'Gmapping',
      'Occupancy grids',
      'TurtleBot3'
    ],
    code: null,
    website: null,
    overview: [
      'Reliable localisation lets a robot estimate pose for navigation; mapping builds a usable model of free and occupied space from sensors.',
      'This work covers two complementary pieces on TurtleBot3 in ROS/Gazebo: comparing AMCL and Gmapping localisation against ground truth, and building a 2D occupancy grid from laser scans and odometry.'
    ],
    approach: [
      'Localisation: run AMCL (particle filter on a known map) and Gmapping (SLAM) on identical trajectories, log pose error vs Gazebo ground truth, and visualise estimates in RViz.',
      'Mapping: for each LaserScan, transform beams into the map frame, update free-space cells along rays and occupied cells at endpoints with Bayesian occupancy updates, then publish an OccupancyGrid.'
    ],
    results: [
      'Both AMCL and Gmapping reached low, stable errors after particle-filter convergence. AMCL was more consistent on a known map; Gmapping suits map creation, with occasional drift during SLAM.',
      'A practical workflow is Gmapping for map creation followed by AMCL for operational localisation. The occupancy-grid mapper produced RViz-visible maps of free, occupied, and unknown space.'
    ],
    media: {
      images: [
        { label: 'RViz occupancy map', ready: false },
        { label: 'Localisation error plots', ready: false }
      ],
      videos: [
        { label: 'Mapping in RViz', ready: false },
        { label: 'AMCL particle convergence', ready: false }
      ]
    }
  },
  {
    slug: 'dmu-knowledge-rag',
    title: 'DMU Knowledge RAG',
    description:
      'Retrieval-augmented Q&A over publicly crawled De Montfort University content, grounding LLM answers in institutional source documents.',
    image: imgDmuRag,
    tech_stack: ['RAG', 'NLP', 'Web crawling', 'Embeddings', 'Python'],
    code: null,
    website: null,
    overview: [
      'Institutional websites hold policies, programmes, and guidance that change over time. A plain LLM answer can drift from the published source; RAG grounds generation in retrieved passages.',
      'This NLP project builds a retrieval-augmented pipeline over publicly available De Montfort University content collected by crawling.'
    ],
    approach: [
      'Crawl public DMU pages, chunk and embed documents, and retrieve relevant passages for a user query before generating an answer.',
      'The system is designed so responses can be checked against the retrieved context, a direct precursor to the thesis focus on structured, long-term memory for LLMs.'
    ],
    results: [
      'A working RAG stack over crawled DMU material for grounded Q&A.',
      'Screenshots and a short demo video will replace the placeholders below when packaged for the portfolio.'
    ],
    media: {
      images: [
        { label: 'Query + retrieved passages', ready: false },
        { label: 'Pipeline diagram', ready: false }
      ],
      videos: [{ label: 'Live Q&A demo', ready: false }]
    }
  }
]

export function getFeaturedProject (slug) {
  return featuredProjects.find((p) => p.slug === slug) || null
}
